/* Simple SVG donut chart with hover labels.
 * Usage: <DonutChart data={[{label,value,color}, ...]} title="..." />
 */
export default function DonutChart({ data, title, size = 160, align = "center" }) {
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 4;
  const innerR = r * 0.55;

  let acc = 0;
  const segments = data.map((d) => {
    const value = d.value || 0;
    const startAngle = total > 0 ? (acc / total) * Math.PI * 2 : 0;
    acc += value;
    const endAngle = total > 0 ? (acc / total) * Math.PI * 2 : 0;
    const isFullCircle = total > 0 && value === total;
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
    const x1 = cx + r * Math.sin(startAngle);
    const y1 = cy - r * Math.cos(startAngle);
    const x2 = cx + r * Math.sin(endAngle);
    const y2 = cy - r * Math.cos(endAngle);
    const xi1 = cx + innerR * Math.sin(endAngle);
    const yi1 = cy - innerR * Math.cos(endAngle);
    const xi2 = cx + innerR * Math.sin(startAngle);
    const yi2 = cy - innerR * Math.cos(startAngle);
    let path = "";
    if (isFullCircle) {
      // Two semicircles (avoid degenerate start==end SVG arc)
      const xMid = cx + r * Math.sin(Math.PI);
      const yMid = cy - r * Math.cos(Math.PI);
      const xMidI = cx + innerR * Math.sin(Math.PI);
      const yMidI = cy - innerR * Math.cos(Math.PI);
      path = `M ${x1} ${y1} A ${r} ${r} 0 1 1 ${xMid} ${yMid} A ${r} ${r} 0 1 1 ${x1} ${y1} Z M ${xi2} ${yi2} A ${innerR} ${innerR} 0 1 0 ${xMidI} ${yMidI} A ${innerR} ${innerR} 0 1 0 ${xi2} ${yi2} Z`;
    } else if (total > 0 && value > 0) {
      path = `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} L ${xi1} ${yi1} A ${innerR} ${innerR} 0 ${largeArc} 0 ${xi2} ${yi2} Z`;
    }
    const pct = total > 0 ? Math.round((value / total) * 100) : 0;
    return { ...d, path, pct };
  });

  return (
    <div className="bg-slate-50 rounded-xl border border-slate-100 p-4 flex flex-col" data-testid={`donut-${title}`}>
      <h3 className="text-sm font-semibold text-slate-700 mb-3">{title}</h3>
      <div className={`flex items-center gap-5 ${align === "left" ? "justify-start" : "justify-center"}`}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="flex-shrink-0">
          {total === 0 ? (
            <>
              <circle cx={cx} cy={cy} r={r} fill="#e2e8f0" />
              <circle cx={cx} cy={cy} r={innerR} fill="white" />
              <text x={cx} y={cy + 4} textAnchor="middle" className="text-[10px] fill-slate-400">
                no data
              </text>
            </>
          ) : (
            segments.map((s) => (
              <path key={`seg-${s.label}`} d={s.path} fill={s.color}>
                <title>{`${s.label}: ${s.value} (${s.pct}%)`}</title>
              </path>
            ))
          )}
        </svg>
        <ul className="space-y-1 text-xs">
          {segments.map((s) => (
            <li key={`legend-${s.label}`} className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                <span className="text-slate-600">{s.label}</span>
              </div>
              <span className="text-slate-500 tabular-nums ml-auto font-medium">
                {s.value}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
